import { useState } from 'react';
import { Box, Button } from '@mui/material';
import STATES from '../../../constants/states';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import { addBusinessTripSchema } from '../../../types/businesses';
import { TIMEZONES } from '../../../constants/timezones';
import { handleAddBusinessTrip } from '../../../helpers/businesses';
import ReuseableTable from '../../table/ReuseableTable';
import {
  headerCells,
  createRows,
} from '../../../utils/table/businessTripsTable';
import { important } from '../../../utils/global/helper';

interface TripState {
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  startDateTime: string;
  endDateTime: string;
  studioName?: string;
}

function BusinessTrips() {
  const [form, setForm] = useState<FormData | null>(null);

  const { user, reloadUser, isLoading } = useUser<LoadedUser>();

  if (!user?.business) return null;

  const { business } = user;

  const initialValues = {
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    timezone: '',
    startDateTime: '',
    endDateTime: '',
    studioName: '',
  } as TripState;

  const onSuccess = async () => {
    await reloadUser();
    setForm(null);
  };

  const tripForm: FormData<TripState> = {
    title: 'Add a business trip',
    initialValues,
    validationSchema: addBusinessTripSchema,
    submitButtonText: 'Add trip',
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Studio name',
          name: 'studioName',
          placeholder: 'Main St. Tattoos',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'Street address',
          name: 'streetAddress',
          placeholder: '123 Main St.',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'Floor, suite, etc.',
          name: 'streetAddress2',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'City',
          name: 'city',
          placeholder: 'Los Angeles',
        },
      ],
      [
        {
          inputType: 'select',
          label: 'State',
          name: 'state',
          placeholder: 'Select state',
          options: STATES.map(state => ({
            value: state.abbreviation,
            label: state.name,
          })),
        },
        {
          inputType: 'text',
          label: 'Zip',
          name: 'zip',
          placeholder: '90001',
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Timezone',
          name: 'timezone',
          placeholder: 'Select timezone',
          options: TIMEZONES,
        },
      ],
      [
        {
          inputType: 'birthday',
          label: 'Trip start date',
          name: 'startDateTime',
        },
      ],
      [
        {
          inputType: 'birthday',
          label: 'Trip end date',
          name: 'endDateTime',
        },
      ],
    ],
    onSubmit: async values => {
      const response = await handleAddBusinessTrip(business.id, {
        streetAddress: values.streetAddress ?? undefined,
        streetAddress2: values.streetAddress2 ?? undefined,
        city: values.city ?? undefined,
        state: values.state ?? undefined,
        zip: values.zip ?? undefined,
        timezone: values.timezone ?? undefined,
        startDateTime: new Date(values.startDateTime) ?? undefined,
        endDateTime: new Date(values.endDateTime) ?? undefined,
        studioName: values.studioName ?? undefined,
      });
      if (response) {
        await onSuccess();
      }
    },
  };

  const tripsRows = createRows({ items: business.businessTrips });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}>
      <Box
        className="header-right"
        sx={{
          display: {
            xs: 'inline',
            md: 'none',
          },
        }}>
        <Button
          variant="contained"
          className="primary hidden-desktop"
          onClick={() => setForm(tripForm)}>
          Add Trip
        </Button>
      </Box>
      <div className="calendar-header">
        <Box
          className="header-right"
          sx={{
            display: {
              xs: important('none'),
              md: important('inline'),
            },
          }}>
          <Button
            variant="contained"
            className="primary"
            onClick={() => setForm(tripForm)}>
            Add Trip
          </Button>
        </Box>
      </div>
      <ReuseableTable
        name="Business Trips"
        rows={tripsRows}
        headerCells={headerCells}
        emptyMessage="No business trips to show right now. If you believe this is an error, please reach out to our support team."
        loading={isLoading}
      />
      <FormSmallModal open={!!form} onClose={() => setForm(null)} form={form} />
    </Box>
  );
}

export default BusinessTrips;
